import React, { JSX, ReactNode } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import Tooltip from '@mui/material/Tooltip'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import { grey } from '@mui/material/colors'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import useMediaQuery from '@mui/material/useMediaQuery'
import { CalendarTableCell } from './CalendarTableCell'
import { ExtendedWarrantEntity, getCellDates } from './utils'
import { IssueEntity } from 'types/IssueEntity'
import { formatMoney, formatNumber } from '../../packages/utils/numberFormatters'
import { formatWarrantSubPrice } from '../../components/entities/WarrantSubPrice'
import * as sharesOrUnits from '../../utils/issues/sharesOrUnits.js'
import { WarrantName } from '../../components/entities/WarrantName'
import IssueTypeLabels, { HasWarrantLabel } from '../../components/entities/IssueTypeLabels'

import './CalendarTableRow.css'

const RowTooltip = ({
    entity,
    locale,
    children,
}: {
    entity: IssueEntity | ExtendedWarrantEntity
    locale: string
    children: JSX.Element
}) => {
    let unitContent: JSX.Element | null = null

    const subPrice = (() => {
        if ('type' in entity) {
            const isIpoWithIntervalSubPrice =
                entity.type === 'IPO' &&
                !entity.unitSubPrice &&
                entity.ipoIntervalHigh !== null &&
                entity.ipoIntervalLow !== null

            if (isIpoWithIntervalSubPrice) {
                return `${formatNumber(entity.ipoIntervalLow, { decimals: 2, locale })} - ${formatNumber(entity.ipoIntervalHigh, { decimals: 2, locale })} ${entity.currency}`
            }
            return formatMoney(entity.unitSubPrice, {
                locale,
                currencySymbol: entity.currency,
                minDecimals: 2,
                maxDecimals: 5,
            })
        }

        return formatWarrantSubPrice(
            { ...entity, currentSubscriptionPeriod: entity.activeSubscriptionPeriods[0] },
            locale,
            entity.currency,
        )
    })()

    if ('type' in entity && sharesOrUnits.isUnit(entity)) {
        const instrumentElements: ReactNode[] = []
        if (entity.warrants && entity.warrants.length > 0) {
            entity.warrants.forEach((warrant) => {
                const hasWarrantsPerUnitValue = warrant.warrantsPerUnit !== null && warrant.warrantsPerUnit !== 0

                instrumentElements.push(
                    <Typography key={warrant.id}>
                        {hasWarrantsPerUnitValue &&
                            sharesOrUnits.warrantsQuantityLabel(warrant.warrantsPerUnit, locale)}
                        {!hasWarrantsPerUnitValue && 'X teckningsoptioner'}: <WarrantName warrant={warrant} />
                    </Typography>,
                )
            })
        }
        if (entity.unitExtraContent) {
            instrumentElements.push(
                <Typography key='unitExtraContent'>
                    {entity.unitExtraContent}
                    <br />
                </Typography>,
            )
        }

        unitContent = (
            <Box>
                {entity.sharesInUnit !== null && sharesOrUnits.sharesQuantityLabel(entity.sharesInUnit, locale)}
                {entity.sharesInUnit === null && 'X aktier'}
                <br />
                {instrumentElements}
            </Box>
        )
    }

    const financialAdvisors =
        entity.financialAdvisors && entity.financialAdvisors.length
            ? entity.financialAdvisors.map((advisor) => advisor.name).join(', ')
            : 'Ingen finansiell rådgivare'

    return (
        <Tooltip
            title={
                <Box>
                    <Typography>
                        Teckningskurs: {subPrice}{' '}
                        {'type' in entity ? `per ${sharesOrUnits.isUnit(entity) ? 'unit' : 'aktie'}` : ''}
                    </Typography>
                    {'type' in entity && sharesOrUnits.isUnit(entity) ? (
                        <Stack direction='row' spacing={1}>
                            <Typography>Unit:</Typography>
                            <Typography component='div'>{unitContent}</Typography>
                        </Stack>
                    ) : null}
                    <Typography>Lista: {entity.marketplaceName}</Typography>
                    <Typography>Finansiell rådgivare: {financialAdvisors}</Typography>
                </Box>
            }
            followCursor
            slotProps={{ tooltip: { className: 'calendar-table-tooltip' } }}
        >
            {children}
        </Tooltip>
    )
}

export const CalendarTableRow = ({
    datesList,
    entity,
    isSubStartDateBeforeCalendarStart,
    isSubEndDateBeforeCalendarStart,
    isSubEndDateAfterCalendarEnd,
    size,
    showPrelPrice,
    subStartDate,
    subEndDate,
    subLabelDate,
    onMouseLeave,
    onMouseOver,
}: {
    datesList: Date[]
    entity: ExtendedWarrantEntity | IssueEntity
    isSubStartDateBeforeCalendarStart: boolean
    isSubEndDateBeforeCalendarStart: boolean
    isSubEndDateAfterCalendarEnd: boolean
    size: ReactNode
    showPrelPrice: boolean
    subStartDate: Date
    subEndDate: Date
    subLabelDate: Date
    onMouseLeave: (e: React.MouseEvent<HTMLTableCellElement>) => void
    onMouseOver: (e: React.MouseEvent<HTMLTableCellElement>) => void
}) => {
    const isSmallScreen = useMediaQuery('(max-width: 960px)')
    const { i18n } = useTranslation()
    const locale = i18n.language
    const entityId = entity.id
    const entityType = 'type' in entity ? 'issue' : 'warrant'

    const handleClick = React.useCallback(() => {
        const path = entityType === 'issue' ? `/issues/${entityId}` : `/warrants/${entityId}`

        window.open(path, '_blank')
    }, [entityId, entityType])

    return (
        <RowTooltip key={entity.id} entity={entity} locale={locale}>
            <TableRow className='calendar-table-row' onClick={handleClick}>
                <TableCell
                    className='calendar-table-first-cell'
                    onMouseOver={onMouseOver}
                    onMouseLeave={onMouseLeave}
                    sx={{ left: isSmallScreen ? '0px' : '215px' }}
                >
                    <Stack
                        sx={{
                            padding: '10px 16px 8px 16px',
                            borderRight: `1px solid ${grey[300]}`,
                        }}
                    >
                        <Stack
                            direction='row'
                            spacing={2}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            {'type' in entity ? (
                                <IssueTypeLabels issue={entity} />
                            ) : (
                                <HasWarrantLabel key='hasWarrants' />
                            )}
                            <Link to={''}>
                                {'type' in entity
                                    ? entity.shareName
                                    : entity.instrumentName || `${entity.companyName} ${entity.name}`}
                            </Link>
                        </Stack>
                        <Typography variant='caption'>
                            {size}
                            {showPrelPrice ? '*' : null}
                        </Typography>
                    </Stack>
                </TableCell>
                {datesList.map((date, index) => {
                    const {
                        isExRightsDate,
                        isIncRightsDate,
                        isRightsEndDate,
                        isIpoTradingDate,
                        isSubDate,
                        isSubStartDate,
                        isSubEndDate,
                        showSubLabel,
                    } = getCellDates(
                        entity,
                        subStartDate,
                        subEndDate,
                        subLabelDate,
                        date,
                        !!isSubEndDateBeforeCalendarStart,
                    )

                    return (
                        <CalendarTableCell
                            key={`${entity.id}_${date.toISOString()}`}
                            colNumber={index + 2}
                            date={date.toISOString()}
                            isExRightsDate={!!isExRightsDate}
                            isIncRightsDate={!!isIncRightsDate}
                            isRightsEndDate={!!isRightsEndDate}
                            isIpoTradingDate={!!isIpoTradingDate}
                            isSubDate={isSubDate}
                            isSubEndDateAfterCalendarEnd={!!isSubEndDateAfterCalendarEnd}
                            isSubStartDateBeforeCalendarStart={!!isSubStartDateBeforeCalendarStart}
                            isSubStartDate={isSubStartDate}
                            isSubEndDate={isSubEndDate}
                            onMouseLeave={onMouseLeave}
                            onMouseOver={onMouseOver}
                            showSubLabel={showSubLabel}
                        />
                    )
                })}
            </TableRow>
        </RowTooltip>
    )
}
